import { reportError } from "@/components/ClientErrorReporter";
import { isStaleChunkError } from "@/lib/error-triage";

/**
 * Shared recovery used by every route-segment `error.tsx`.
 *
 * Two jobs:
 *  1. Self-heal "stale chunk" failures. After a deploy, a tab that was open
 *     before (or navigates from a cached page) tries to fetch JS chunks whose
 *     hashed filenames no longer exist — a dynamic import / lazy component then
 *     rejects with ChunkLoadError / "Failed to fetch dynamically imported
 *     module". React's `reset()` only re-renders against the SAME stale
 *     references, so it can't recover; a full page reload pulls the fresh
 *     assets. We reload ONCE (guarded via sessionStorage) so a
 *     genuinely-broken page can't reload-loop.
 *  2. Otherwise, log the error to the `app_errors` table so /admin/errors sees
 *     boundary failures.
 *
 * "Is this a stale chunk?" is answered by `isStaleChunkError` in
 * `lib/error-triage.ts`, shared with the global reporter so the question can
 * only ever have one answer. Note a bare `Failed to fetch` used to match here
 * and did real damage: ANY ordinary API failure reaching a boundary — a 500
 * from D1, a dropped mobile connection — was misread as a stale deploy and
 * triggered a full page reload instead of being logged, then was only recorded
 * on its second occurrence within 15s, unlabelled.
 *
 * Returns true when a reload was triggered (the caller may render a minimal
 * "reloading…" state instead of the error UI).
 */
const RELOAD_FLAG = "fm:boundary-reloaded-at";
const RELOAD_COOLDOWN_MS = 15_000;

export function isChunkLoadError(error: { message?: string; stack?: string } | undefined): boolean {
  if (!error) return false;
  return isStaleChunkError(error.message ?? "", error.stack ?? null);
}

export function recoverOrReport(
  error: (Error & { digest?: string }) | undefined,
  source: "react-boundary" = "react-boundary"
): boolean {
  if (typeof window === "undefined") return false;

  if (isChunkLoadError(error)) {
    try {
      const last = Number(window.sessionStorage.getItem(RELOAD_FLAG) ?? "0");
      if (!Number.isFinite(last) || Date.now() - last > RELOAD_COOLDOWN_MS) {
        window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
        window.location.reload();
        return true;
      }
    } catch {
      // sessionStorage blocked (private mode) — fall through to reporting.
    }
  }

  console.error(error);
  if (error) {
    reportError({
      source,
      trigger: "react-boundary",
      message: error.message || "Render error",
      stack: error.stack ?? null,
      // Report the PAGE explicitly. Without it the reporter falls back to
      // `window.location.href` anyway, but being explicit keeps the boundary's
      // intent obvious and pairs the route with the digest for server lookup.
      url: window.location.href,
      context: {
        route: window.location.pathname,
        ...(error.digest ? { digest: error.digest } : {}),
      },
    });
  }
  return false;
}
